import type { Components, Theme } from '@mui/material/styles';

import { ELEVATION, SHAPE, tinted, token } from './scales';

/** Returns the Material 3 tonal look: the brand tint with its border. */
function tonal(theme: Theme) {
  return {
    border: '1px solid',
    ...tinted(theme, 'brand'),
    '&:hover': { backgroundColor: theme.vars.palette.tint.brand.hover },
  };
}

/** Returns the quiet look: neutral text that fills on hover. */
function quiet(theme: Theme) {
  return {
    color: theme.vars.palette.grey[700],
    '&:hover': { backgroundColor: theme.vars.palette.action.hover },
  };
}

/** The kit buttons: filled, tonal, quiet and danger, and links. Keyboard focus shows one ring, not a ripple. */
export const BUTTONS: Components<Theme> = {
  MuiButtonBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-focusVisible': { outline: `2px solid ${theme.vars.palette.text.brand}`, outlineOffset: 2 },
      }),
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true, disableFocusRipple: true },
    styleOverrides: { root: { borderRadius: SHAPE.full } },
    variants: [
      { props: { variant: 'tonal' }, style: ({ theme }) => tonal(theme) },
      { props: { variant: 'text', color: 'inherit' }, style: ({ theme }) => quiet(theme) },
    ],
  },
  MuiIconButton: {
    defaultProps: { disableFocusRipple: true },
    styleOverrides: {
      colorInherit: ({ theme }) => quiet(theme),
      colorPrimary: ({ theme }) => tonal(theme),
      colorError: ({ theme }) => ({
        color: theme.vars.palette.error.contrastText,
        backgroundColor: theme.vars.palette.error.main,
        '&:hover': { backgroundColor: theme.vars.palette.error.dark },
      }),
    },
  },
  MuiLink: {
    styleOverrides: {
      root: ({ theme }) => ({ color: theme.vars.palette.text.brand, textDecorationColor: 'currentColor' }),
    },
  },
  KitIconButton: {
    variants: [
      { props: { raised: true }, style: { boxShadow: ELEVATION.sm } },
      {
        props: { danger: 'quiet' },
        style: ({ theme }) => ({ '&:hover': tinted(theme, 'danger') }),
      },
      {
        props: { danger: 'overlay' },
        style: ({ theme }) => ({
          border: `1px solid ${theme.vars.palette.divider}`,
          backgroundColor: token('surface', 0.85),
          backdropFilter: 'blur(8px)',
          '&:hover': tinted(theme, 'danger'),
        }),
      },
    ],
  },
};
